// スポットの手数料自動考慮 (AC-BSP-004b / AC-BSP-106) のプレビュー RPC (`PreviewBiddingSpotBidFeeAdjustedPrices`)
// の応答を、 時間前と共通の判定 (`~/models/feeAutoConsiderPreview`) に渡す写像。 入札価格の算出と値域・入札制限の
// 判定は backend が持つ (#8109)。
import { Code, ConnectError } from '@connectrpc/connect'
import type { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import {
  BiddingSpotFeeAdjustedPriceBound,
  BiddingSpotFeeAdjustedPriceBoundSource,
  type BiddingSpotFeeAdjustedPriceResult,
  BiddingSpotFeeAdjustedPriceStatus,
  type BiddingSpotFeeAutoConsiderState,
  SpotBidWriteBlockedReason,
  SpotBidWriteBlockedReasonSchema,
} from '@ebalance/proto/bffuser/v1/bidding_spot_model_pb'
import type { TFunction } from 'i18next'

import { readAppErrorInfo } from '~/libs/appErrorInfo'
import {
  type FeeAdjustedPricePreviewQueryStatus,
  type FeeAdjustedPriceResult,
  type FeeAdjustmentViolation,
  type FeeAutoConsiderConfirmed,
  type FeeAutoConsiderHint,
  type FeeAutoConsiderPreview,
  type FeeAutoConsiderTreatment,
  feeAdjustmentViolationLines,
  resolveFeeAutoConsiderPreview,
} from '~/models/feeAutoConsiderPreview'
import {
  JEPX_PRICE_CONSTRAINT,
  isPlainDecimalInput,
} from '~/models/numberInputConstraint'
import type { SpotFeeAutoConsider } from '~/pages/SpotBidding/models/spotFeeAutoConsider'

const STATUS: Record<
  BiddingSpotFeeAdjustedPriceStatus,
  FeeAdjustedPriceResult['status']
> = {
  [BiddingSpotFeeAdjustedPriceStatus.UNSPECIFIED]: undefined,
  [BiddingSpotFeeAdjustedPriceStatus.OFF]: 'off',
  [BiddingSpotFeeAdjustedPriceStatus.ADJUSTED]: 'adjusted',
  [BiddingSpotFeeAdjustedPriceStatus.OUT_OF_RANGE]: 'outOfRange',
  [BiddingSpotFeeAdjustedPriceStatus.INDETERMINATE]: 'indeterminate',
}

const BOUND: Record<
  BiddingSpotFeeAdjustedPriceBound,
  FeeAdjustmentViolation['bound'] | undefined
> = {
  [BiddingSpotFeeAdjustedPriceBound.UNSPECIFIED]: undefined,
  [BiddingSpotFeeAdjustedPriceBound.MIN]: 'min',
  [BiddingSpotFeeAdjustedPriceBound.MAX]: 'max',
}

const SOURCE: Record<
  BiddingSpotFeeAdjustedPriceBoundSource,
  FeeAdjustmentViolation['source'] | undefined
> = {
  [BiddingSpotFeeAdjustedPriceBoundSource.UNSPECIFIED]: undefined,
  [BiddingSpotFeeAdjustedPriceBoundSource.JEPX]: 'jepx',
  [BiddingSpotFeeAdjustedPriceBoundSource.BID_LIMIT]: 'bidLimit',
}

const toFeeResult = (
  result: BiddingSpotFeeAdjustedPriceResult,
): FeeAdjustedPriceResult => ({
  status: STATUS[result.status],
  orderPriceYenPerKwh: result.orderPriceYenPerKwh?.value,
  violation:
    result.violation === undefined
      ? undefined
      : {
          bound: BOUND[result.violation.bound],
          source: SOURCE[result.violation.source],
          boundOrderPriceYenPerKwh:
            result.violation.boundOrderPriceYenPerKwh?.value,
          desiredPriceBoundYenPerKwh:
            result.violation.desiredPriceBoundYenPerKwh?.value,
        },
})

/**
 * 開いているモーダルの扱い (`useSpotFeeAutoConsider`。 欄名と OFF の固定の源) を、 プレビューと突き合わせる
 * 扱いに写す。 `fallbackOff` は設定が ON と分かったうえで単価を使えない間なので、 案内を出し分ける
 * 設定は ON とする。
 */
export const spotFeeTreatment = (
  fee: SpotFeeAutoConsider,
): FeeAutoConsiderTreatment => {
  switch (fee.kind) {
    case 'loading':
      return { status: 'loading' }
    case 'unavailable':
      return { status: 'error' }
    case 'off':
      return { status: 'success', enabled: false }
    case 'on':
      return { status: 'success', enabled: true }
    case 'fallbackOff':
      return {
        status: 'fallbackOff',
        setting: { status: 'success', enabled: true },
      }
  }
}

/** スポットの応答を共通の判定に渡す。 */
export const resolveSpotFeeAutoConsiderPreview = ({
  status,
  state,
  result,
  side,
  treatment,
}: {
  status: FeeAdjustedPricePreviewQueryStatus
  state: BiddingSpotFeeAutoConsiderState | undefined
  result: BiddingSpotFeeAdjustedPriceResult | undefined
  side: Side
  treatment: FeeAutoConsiderTreatment
}): FeeAutoConsiderPreview =>
  resolveFeeAutoConsiderPreview({
    status,
    state:
      state === undefined
        ? undefined
        : {
            enabled: state.enabled,
            feeYenPerKwh: state.feeYenPerKwh?.value,
            unavailable: state.unavailable,
          },
    result: result === undefined ? undefined : toFeeResult(result),
    side,
    treatment,
  })

/**
 * 価格欄の値を、 プレビューに問い合わせて送る 10 進数の文字列にする。 送れない形は undefined。
 * `<input type="number">` は指数表記を通す (文字列で持つ欄は `1e2`、 数値で持つ欄は `String(1e-7)` = "1e-7") が、
 * backend は受け付けず、 プレビューの入力エラーを「手数料単価を使えない」と読むと閉じるまで OFF に固定してしまう。
 * 個別作成の 2 モーダルは、 プレビューに載せる前とも送る前とも、 これで判定する (送る前は範囲外の項目エラー。
 * 時間前の `intradayPriceViolation` と同じ扱い)。
 */
export const spotPriceEntryText = (
  value: string | number | undefined,
): string | undefined => {
  if (value === undefined) return undefined
  const text = typeof value === 'number' ? String(value) : value
  return isPlainDecimalInput(text) ? text : undefined
}

/** AC-BSP-106 の 1 行目と 2 行目。 補足表示の欄は 2 行で出す。 */
export const spotFeeAdjustmentViolationLines = (
  t: TFunction,
  v: FeeAdjustmentViolation,
): { line1: string; line2: string } => feeAdjustmentViolationLines(t, v)

/** 登録時に価格欄へ出す AC-BSP-106 の文言。 design は 1 行目と 2 行目を半角スペースで 1 文につなぐ。 */
export const spotFeeAdjustmentFieldError = (
  t: TFunction,
  v: FeeAdjustmentViolation,
): string => {
  const { line1, line2 } = spotFeeAdjustmentViolationLines(t, v)
  return `${line1} ${line2}`
}

/** 補足表示の種類を個別作成の文言 key に写す (AC-BSP-004b。 範囲外は 2 行の文言を別に出す)。 */
const HINT_KEYS: Record<FeeAutoConsiderHint['key'], string> = {
  feeAutoConsiderLoading: 'page:biddingSpot.form.feeAutoConsiderLoadingHint',
  feeAutoConsiderUnavailable:
    'page:biddingSpot.form.feeAutoConsiderUnavailableHint',
  feeAutoConsiderSettingMismatch:
    'page:biddingSpot.form.feeAutoConsiderSettingMismatchHint',
  feeAutoConsiderSettingUnknown:
    'page:biddingSpot.form.feeAutoConsiderUnavailableHint',
  feeAutoConsiderAdjusted: 'page:biddingSpot.form.feeAutoConsiderOnPreview',
  feeAutoConsiderIncomplete:
    'page:biddingSpot.form.feeAutoConsiderOnIncomplete',
  feeAutoConsiderOff: 'page:biddingSpot.form.feeAutoConsiderOffHint',
  feeAutoConsiderFallbackOff:
    'page:biddingSpot.form.feeAutoConsiderFallbackOffHint',
}

export const spotFeeHintKey = (hint: FeeAutoConsiderHint): string =>
  HINT_KEYS[hint.key]

/**
 * 作成・下書きの保存が手数料の理由で拒否されたか。 開いたままのモーダルが突き合わせているプレビューと
 * 設定を取り直す引き金 (拒否の文言「表示し直した入札価格を確認してから…」 を成り立たせる)。
 */
export function isSpotFeePreviewStale(err: unknown): boolean {
  if (!(err instanceof ConnectError) || err.code !== Code.FailedPrecondition) {
    return false
  }
  const reason = readAppErrorInfo(err, SpotBidWriteBlockedReasonSchema)?.reason
  return (
    reason === SpotBidWriteBlockedReason.FEE_AUTO_CONSIDER_CHANGED ||
    reason === SpotBidWriteBlockedReason.FEE_ADJUSTED_PRICE_OUT_OF_RANGE
  )
}

/** 個別作成の登録で、 価格について次にすること。 */
export type SpotFeeSubmission =
  /** 価格欄の項目エラーを出して送らない。 */
  | { kind: 'error'; message: string }
  /** 送る。 `feeAutoConsider` があれば入力値は希望価格、 無ければ入札価格。 */
  | { kind: 'send'; feeAutoConsider: FeeAutoConsiderConfirmed | undefined }
  /** まだ送れない (プレビュー待ち・食い違い。 登録ボタンは押せない)。 */
  | { kind: 'hold' }

/**
 * 個別作成の 2 モーダルが登録する時の、 価格の扱いを 1 か所で決める (AC-BSP-004b / AC-BSP-106 / AC-BSP-062)。
 * 入札価格の算出と値域・入札制限の判定は backend のプレビューが持ち、 ここはその判定と入力の形を送信に写す。
 * ON の範囲外はプレビューの AC-BSP-106 の文言、 それ以外の送れない形 (指数表記・OFF の値域外・backend が
 * 入力エラーで拒否した値) は AC-BSP-078 の範囲外の共通文言。
 */
export const resolveSpotFeeSubmission = (
  t: TFunction,
  {
    price,
    isMarket,
    fee,
    preview,
    rejectedByBackend,
  }: {
    /** 価格欄の値 (文字列で持つ欄・数値で持つ欄のどちらも)。 */
    price: string | number | undefined
    isMarket: boolean
    fee: SpotFeeAutoConsider
    preview: FeeAutoConsiderPreview
    /** プレビューが入力エラー (InvalidArgument) を返した ([[useSpotFeeAutoConsiderPreview]] の `invalidInput`)。 */
    rejectedByBackend: boolean
  },
): SpotFeeSubmission => {
  if (isMarket) return { kind: 'send', feeAutoConsider: undefined }
  const priceRange = (): SpotFeeSubmission => ({
    kind: 'error',
    message: t('page:biddingCommon.validation.priceRange'),
  })
  const entry = spotPriceEntryText(price)
  if (entry === undefined) return priceRange()
  const value = Number(entry)
  // ON の希望価格そのものの範囲 (負数・0・999 超) では止めず、 プレビューが算出した入札価格で判定する
  // (#11466 Q117 = B / Q117-2)。 OFF は入力値が入札価格なので値域で止める。
  if (
    fee.kind !== 'on' &&
    (value < JEPX_PRICE_CONSTRAINT.min || value > JEPX_PRICE_CONSTRAINT.max)
  ) {
    return priceRange()
  }
  if (rejectedByBackend) return priceRange()
  switch (preview.kind) {
    case 'outOfRange':
      return {
        kind: 'error',
        message: spotFeeAdjustmentFieldError(t, preview.violation),
      }
    case 'ready':
      return { kind: 'send', feeAutoConsider: preview.confirmed }
    case 'fallbackOff':
      return { kind: 'send', feeAutoConsider: undefined }
    case 'blocked':
    case 'idle':
      return { kind: 'hold' }
  }
}

type OutOfRangePreview = Extract<FeeAutoConsiderPreview, { kind: 'outOfRange' }>

/**
 * 複数エリアのプレビューのうち範囲外のものから、 価格欄の案内と footer の理由に出す 1 件を選ぶ (AC-BSP-106 ※
 * 「複数の契機に同時に該当する場合は希望価格の入力可能範囲が最も狭くなる契機」)。 一括作成は 1 つの価格欄が
 * エリアごとのプレビューに当たるため、 緩い側を案内して直しても別のエリアで再びエラーになるのを避ける。
 *
 * 下限側 (`bound === 'min'`) は希望価格の下限が最大のもの、 上限側 (`'max'`) は上限が最小のものを選ぶ。
 * 下限側と上限側が両方ある時は下限側を優先する (どちらを案内しても直し切れないため、 結果を固定するだけの
 * 決め)。 値は backend が返したものから選ぶだけで、 業務の計算はしない。 範囲外が無ければ undefined。
 */
export const narrowestOutOfRangePreview = (
  previews: readonly FeeAutoConsiderPreview[],
): OutOfRangePreview | undefined => {
  const outOfRange = previews.filter(
    (preview): preview is OutOfRangePreview => preview.kind === 'outOfRange',
  )
  const pick = (
    candidates: readonly OutOfRangePreview[],
    isBetter: (candidate: number, current: number) => boolean,
  ): OutOfRangePreview | undefined =>
    candidates.reduce<OutOfRangePreview | undefined>((current, candidate) => {
      if (current === undefined) return candidate
      const next = Number(candidate.violation.desiredPriceBoundYenPerKwh)
      const prev = Number(current.violation.desiredPriceBoundYenPerKwh)
      return isBetter(next, prev) ? candidate : current
    }, undefined)
  return (
    pick(
      outOfRange.filter((preview) => preview.violation.bound === 'min'),
      (next, prev) => next > prev,
    ) ??
    pick(
      outOfRange.filter((preview) => preview.violation.bound === 'max'),
      (next, prev) => next < prev,
    )
  )
}

/** プレビューを待つ・食い違う間は、 個別作成の登録ボタンを押せない (確認画面の表示と保存値の一致。 `docs/principles.md` ルール 1)。 */
export const isSpotFeePreviewBlocking = (
  preview: FeeAutoConsiderPreview,
  isMarket: boolean,
): boolean => !isMarket && preview.kind === 'blocked'

/**
 * 入札制限ヒント・売買逆転の判定に使う入札価格 (AC-BSP-107 ※。 上下限価格は入札価格で判定する)。 ON は
 * プレビューの入札価格で、 算出できていない間・範囲外の間は判定しない (希望価格で判定すると手数料ぶんずれる。
 * 範囲外は AC-BSP-106 の案内に任せる)。 それ以外は入力値がそのまま入札価格。
 */
export const spotOrderPriceForChecks = ({
  fee,
  preview,
  enteredPrice,
}: {
  fee: SpotFeeAutoConsider
  preview: FeeAutoConsiderPreview
  enteredPrice: number | undefined
}): number | undefined => {
  if (preview.kind === 'ready' && preview.confirmed.enabled) {
    return preview.orderPriceYenPerKwh !== undefined
      ? Number(preview.orderPriceYenPerKwh)
      : undefined
  }
  return fee.kind === 'on' ? undefined : enteredPrice
}
